export interface DocxBlock {
	type: "heading" | "paragraph" | "listItem" | "tableRow"
	level?: number
	text: string
	cells?: string[]
}

export const DOCX_BLOCK_LIMIT = 400

const TAG = /<(\/?)w:([a-zA-Z]+)([^>]*)>/g

/**
 * Turns the body of a .docx into readable blocks.
 *
 * Word stores text as runs inside paragraphs, so a sentence is split across
 * elements and a naive strip would glue words together. This walks the tags it
 * needs and leaves the rest, which is enough to read a document without
 * pretending to reproduce its layout.
 */
export function parseDocxXml(
	xml: string,
	limit = DOCX_BLOCK_LIMIT,
): DocxBlock[] {
	const body = xml.slice(
		xml.indexOf("<w:body>") + 8,
		xml.lastIndexOf("</w:body>"),
	)
	if (!body) return []

	const blocks: DocxBlock[] = []
	let paragraph = ""
	let style = ""
	let listLevel: number | null = null
	let inTextRun = false
	let cellText: string[] = []
	let inTable = false
	let cellStart = -1

	const flushParagraph = () => {
		const text = collapse(paragraph)
		paragraph = ""
		const currentStyle = style
		const currentList = listLevel
		style = ""
		listLevel = null
		if (!text) return
		if (inTable) {
			cellText.push(text)
			return
		}
		if (currentList !== null) {
			blocks.push({ type: "listItem", level: currentList, text })
			return
		}
		const heading = headingLevel(currentStyle)
		blocks.push(
			heading
				? { type: "heading", level: heading, text }
				: { type: "paragraph", text },
		)
	}

	let match: RegExpExecArray | null
	TAG.lastIndex = 0
	let cursor = 0

	while ((match = TAG.exec(body)) !== null) {
		const [full, closing, name, attributes] = match
		if (inTextRun) {
			paragraph += decodeXml(body.slice(cursor, match.index))
			inTextRun = false
		}

		if (!closing) {
			switch (name) {
				case "t":
					inTextRun = true
					cursor = match.index + full.length
					break
				case "tab":
					paragraph += " "
					break
				case "br":
					paragraph += " "
					break
				case "pStyle":
					style = attribute(attributes ?? "", "w:val") ?? ""
					break
				case "ilvl":
					listLevel = Number(attribute(attributes ?? "", "w:val") ?? "0") || 0
					break
				case "numPr":
					if (listLevel === null) listLevel = 0
					break
				case "tbl":
					inTable = true
					break
				case "tc":
					cellStart = blocks.length
					break
			}
		} else {
			switch (name) {
				case "p":
					flushParagraph()
					break
				case "tc":
					if (cellStart === -1) cellText.push("")
					cellStart = -1
					break
				case "tr":
					if (cellText.length > 0) {
						blocks.push({
							type: "tableRow",
							text: cellText.join(" | "),
							cells: cellText,
						})
						cellText = []
					}
					break
				case "tbl":
					inTable = false
					cellText = []
					break
			}
		}

		if (blocks.length >= limit) break
	}

	flushParagraph()
	return blocks.slice(0, limit)
}

function headingLevel(style: string): number | undefined {
	const match = /^heading\s*([1-6])$/i.exec(style.replace(/[-_]/g, " ").trim())
	if (match?.[1]) return Number(match[1])
	if (/^title$/i.test(style)) return 1
	return undefined
}

function attribute(source: string, name: string): string | undefined {
	const match = new RegExp(`${name}="([^"]*)"`).exec(source)
	return match?.[1]
}

const ENTITIES: Record<string, string> = {
	"&amp;": "&",
	"&lt;": "<",
	"&gt;": ">",
	"&quot;": '"',
	"&apos;": "'",
	"&nbsp;": " ",
}

function decodeXml(value: string): string {
	return value
		.replace(
			/&(amp|lt|gt|quot|apos|nbsp);/g,
			(entity) => ENTITIES[entity] ?? entity,
		)
		.replace(/&#(\d+);/g, (_, code: string) =>
			String.fromCodePoint(Number(code)),
		)
		.replace(/&#x([0-9a-fA-F]+);/g, (_, code: string) =>
			String.fromCodePoint(parseInt(code, 16)),
		)
}

function collapse(value: string): string {
	return value.replace(/\s+/g, " ").trim()
}
